import DOMPurify from 'dompurify';

import { trimExtraSpaces } from 'shared/lib/utils';
import { BOOST_EM_ASSET } from 'shared/config/entitelment-constants';
import { dateService } from 'shared/lib/date';

import {
  BoostEmInsight,
  BoostEmRecommendationOption,
  FilteredBoostEmInsights,
  Insight,
  Recommendation,
} from './types';

/**
 * Insight service return only product_keys that user is entitled to, so we can use it to filter productNames
 */
export const filterProductNames = (
  productNames: { [key: string]: string },
  product_keys: string[],
) => {
  return Object.keys(productNames)
    .filter((key) => product_keys.includes(key))
    .reduce<{ [key: string]: string }>((obj, key) => {
      obj[key] = productNames[key];
      return obj;
    }, {});
};

export const filterUniqueMessages = (
  recommendations: Recommendation[],
): Recommendation[] => {
  const uniqueMessages = new Set();

  return recommendations.filter((recommendation: Recommendation) => {
    const trimmedMessage = trimExtraSpaces(recommendation.content.message);

    if (uniqueMessages.has(trimmedMessage)) {
      return false;
    }

    uniqueMessages.add(trimmedMessage);
    return true;
  });
};

export const sortRecommendations = (
  recommendations: Recommendation[],
): Recommendation[] => {
  return [...recommendations].sort(({ content: a }, { content: b }) => {
    if (a.startDate !== b.startDate) {
      return b.startDate - a.startDate;
    } else {
      return a.message.localeCompare(b.message);
    }
  });
};

export const isBoostEmInsight = (insight: Insight): insight is BoostEmInsight =>
  !!insight.insight_dynamic_content;

export const getFilteredBoostEmInsights = (
  insights: (Insight | null | undefined)[],
): FilteredBoostEmInsights => {
  const initialValue: FilteredBoostEmInsights = {
    fullBoostEmInsights: [],
    genericBoostEmInsights: [],
  };

  return insights.reduce((result, currentInsight) => {
    if (!currentInsight) return result;

    if (!isBoostEmInsight(currentInsight)) return result;

    const hasActualData =
      !currentInsight.insight_dynamic_content.insightButtonLink;

    if (!hasActualData) {
      result.genericBoostEmInsights.push(currentInsight);
      return result;
    }

    result.fullBoostEmInsights.push(currentInsight);

    return result;
  }, initialValue);
};

/**
 * Return only full Boost EM insights if there are such.
 * Otherwise one generic Boost EM insight is returned.
 * */
export const getRelevantBoostEmInsights = (
  filteredBoostEmInsights: FilteredBoostEmInsights,
) => {
  const { fullBoostEmInsights, genericBoostEmInsights } =
    filteredBoostEmInsights;

  if (fullBoostEmInsights.length) {
    return fullBoostEmInsights;
  }

  return genericBoostEmInsights.length ? [genericBoostEmInsights[0]] : [];
};

const sanitizeBoostEmRecommendationTags = (
  recommendationTags: string[],
): string[] => {
  return recommendationTags.map((tag) => DOMPurify.sanitize(tag));
};

// returns only known props to prevent dirty HTML to be used
const sanitizeBoostEmRecommendationOptions = (
  recommendationOptions: BoostEmRecommendationOption[],
): BoostEmRecommendationOption[] => {
  return recommendationOptions.map((recommendationOption) => ({
    id: recommendationOption.id,
    tags: sanitizeBoostEmRecommendationTags(recommendationOption.tags),
    recommendation: DOMPurify.sanitize(recommendationOption.recommendation),
  }));
};

export const normalizeBoostEmInsights = (
  boostEmInsights: BoostEmInsight[],
): Recommendation[] => {
  return boostEmInsights.map((boostEmInsight) => {
    const receivedAt =
      boostEmInsight.insight_content.last_response_metadata?.received_at ??
      null;
    const createdDate = dateService.getStartOfIsoWeekTimestamp(receivedAt);

    return {
      type: boostEmInsight.insight_type,
      app_product_names: [],
      created_date: createdDate,
      // created_date: boostEmInsight.created_timestamp,
      description: '',
      id: boostEmInsight.id,
      start_relevant_date: 0,
      content: {
        productNames: {
          [BOOST_EM_ASSET]: 'CKLA',
        },
        grade: '',
        message: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.header,
        ), // is used for sorting
        actionType: '',
        link: '',
        startDate: createdDate, // is used for sorting
        // startDate: boostEmInsight.created_timestamp, // is used for sorting
        image: '',
      },
      dynamicContent: {
        header: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.header,
        ),
        learnMoreLink: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.learnMoreLink,
        ),
        headline: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.headline,
        ),
        insightButtonLabel: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.insightButtonLabel,
        ),
        insightButtonLink: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.insightButtonLink,
        ),
        thumbnailImgLink: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.thumbnailImgLink,
        ),
        sidebar: DOMPurify.sanitize(
          boostEmInsight.insight_dynamic_content.sidebar,
        ),
        recommendations: sanitizeBoostEmRecommendationOptions(
          boostEmInsight.insight_dynamic_content.recommendations,
        ),
      },
    };
  });
};
